import { BigNumber } from '@ethersproject/bignumber'
import { Token } from '@uniswap/sdk-core'
import { computePoolAddress } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import { V3_CORE_FACTORY_ADDRESSES } from 'constants/addresses'
import { useV3StakerContract } from 'hooks/useContract'
import { useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useMemo } from 'react'
import { PositionDetails } from 'types/position'
import { addHexPrefix } from 'utils/addHexPrefix'

import { useIncentiveConfig } from './useIncentive'
import { useV3Positions } from './useV3Positions'
/**
 * Returns positions that are related to a specific incentive by matching their tokens with the incentive's pool
 * Includes both positions owned directly by the user and positions deposited in the V3 staker contract
 * @param incentiveId The ID of the incentive to filter positions for
 * @returns Filtered positions related to the incentive
 */
export function useAllStakedPositionsForIncentive(incentiveId: string | undefined): {
  loading: boolean
  positions: PositionDetails[] | undefined
} {
  const { chainId } = useWeb3React()
  const v3StakerContract = useV3StakerContract()

  const incentive = useIncentiveConfig(incentiveId)

  const { positions, loading } = useV3Positions(v3StakerContract?.address)

  const stakesArgs = useMemo(() => {
    if (positions?.length && incentiveId) {
      return positions.map((pos) => [pos.tokenId.toString(), addHexPrefix(incentiveId)])
    }
    return []
  }, [positions, incentiveId])

  const stakesResult = useSingleContractMultipleData(v3StakerContract, 'stakes', stakesArgs)
  const stakes = stakesResult.map(({ result }) => {
    return {
      liquidity: result?.liquidity,
      secondsPerLiquidityInsideInitialX128: result?.secondsPerLiquidityInsideInitialX128,
    }
  })
  const stakesLoading = useMemo(() => stakesResult.some(({ loading }) => loading), [stakesResult])

  // Filter positions that match the incentive's pool
  const filteredPositions = useMemo(() => {
    const factoryAddress = chainId ? V3_CORE_FACTORY_ADDRESSES[chainId] : undefined

    if (!positions || !incentive || !chainId || !factoryAddress || !stakes || stakesLoading) return []

    // The incentive contains the pool address directly, but we verify it matches our tokens
    const incentivePoolAddress = incentive.key.pool

    return positions.filter((position, i) => {
      // Create a Token instance for each token in the position (decimals don't matter for pool address computation)
      const tokenA = new Token(chainId, position.token0, 1)
      const tokenB = new Token(chainId, position.token1, 1)

      // Compute the pool address for this position
      const computedPoolAddress = computePoolAddress({
        factoryAddress,
        tokenA,
        tokenB,
        fee: position.fee,
      }).toLowerCase()

      const stakeLiquidity: BigNumber | undefined = stakes[i]?.liquidity

      // Check if the computed pool address matches the incentive's pool address and if position's actually staked
      return (
        computedPoolAddress === incentivePoolAddress.toLowerCase() && (stakeLiquidity ? stakeLiquidity.gt(0) : false)
      )
    })
  }, [positions, incentive, chainId, stakes, stakesLoading])

  return {
    loading: loading || stakesLoading,
    positions: filteredPositions,
  }
}
